import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { KullaniciPersonel, Vardiyali } from '@/types'
import {
  logYaz, degisiklikleriHesapla,
  type LogRol,
} from './sistem-log-service'

const KULLANICILAR = 'kullanicilar'

export interface PersonelOlusturmaData {
  isim: string
  telefon: string
  email?: string
  dogumTarihi: string
  iseBaslamaTarihi: string
  adres?: string
  acilIletisim?: { isim: string; telefon: string }
  pozisyonIdleri: string[]
  subeIdleri: string[]
  calismaTipi: 'fulltime' | 'parttime'
  takvimTipi: 'market' | 'ofis'
  maasTipi: 'saatlik' | 'aylik'
  ucret: number
  fazlaMesaiKatsayisi: 1 | 1.5 | 2
  aylikStandartSaat: number
  sabitEkOdeme: number
  sabitKesinti: number
  extraMesaiYetkisi?: boolean
  ibanNo?: string
  telegramChatId?: string
  yillikIzinBaslangicBakiye: number
  whatsappBildirimi: boolean
}

/** Loglanacak personel alanları (audit trail için izlenir) */
const IZLENEN_ALANLAR = [
  'isim',
  'telefon',
  'email',
  'dogumTarihi',
  'iseBaslamaTarihi',
  'adres',
  'pozisyonIdleri',
  'subeIdleri',
  'calismaTipi',
  'takvimTipi',
  'maasTipi',
  'ucret',
  'fazlaMesaiKatsayisi',
  'aylikStandartSaat',
  'sabitEkOdeme',
  'sabitKesinti',
  'extraMesaiYetkisi',
  'ibanNo',
  'telegramChatId',
  'yillikIzinBaslangicBakiye',
  'whatsappBildirimi',
]

function yeniPersonelId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, KULLANICILAR)).id
}

export async function tumPersonelleriGetir(): Promise<Vardiyali[]> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, KULLANICILAR),
    where('rol', 'in', ['personel', 'sorumlu', 'muhasebe'])
  )
  const snap = await getDocs(q)
  const liste = snap.docs.map(d => ({ id: d.id, ...d.data() }) as Vardiyali)
  return liste.sort((a, b) => a.isim.localeCompare(b.isim, 'tr'))
}

/**
 * Aynı e-posta ile aktif kullanıcı var mı? (BOSS dahil)
 * Auth-side fetchSignInMethodsForEmail enumeration protection yüzünden boş döner;
 * bu fonksiyon Firestore'dan kontrol eder ve pre-check için kullanılır.
 *
 * @param email Tam e-posta (örn. nur@shiftbeat.com)
 * @param haricUid Edit sırasında kendi kaydını hariç tutmak için (opsiyonel)
 */
export async function emailIleKullaniciVarMi(email: string, haricUid?: string): Promise<boolean> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, KULLANICILAR),
    where('email', '==', email.toLowerCase().trim())
  )
  const snap = await getDocs(q)
  return snap.docs.some(d => d.id !== haricUid)
}

export async function personelGetir(id: string): Promise<KullaniciPersonel | null> {
  const db = getFirestoreDb()
  const docSnap = await getDoc(doc(db, KULLANICILAR, id))
  if (!docSnap.exists()) return null
  const data = { id: docSnap.id, ...docSnap.data() } as KullaniciPersonel
  if (data.rol !== 'personel') return null
  return data
}

export async function personelOlustur(
  data: PersonelOlusturmaData,
  olusturanId: string,
  olusturanRol: LogRol = 'boss',
  olusturanIsim?: string,
): Promise<string> {
  const db = getFirestoreDb()
  const id = yeniPersonelId()

  const docData: Record<string, unknown> = {
    rol: 'personel',
    isim: data.isim.trim(),
    telefon: data.telefon.trim(),
    dogumTarihi: data.dogumTarihi,
    iseBaslamaTarihi: data.iseBaslamaTarihi,
    pozisyonIdleri: data.pozisyonIdleri,
    subeIdleri: data.subeIdleri,
    calismaTipi: data.calismaTipi,
    takvimTipi: data.takvimTipi,
    maasTipi: data.maasTipi,
    ucret: data.ucret,
    fazlaMesaiKatsayisi: data.fazlaMesaiKatsayisi,
    aylikStandartSaat: data.aylikStandartSaat,
    sabitEkOdeme: data.sabitEkOdeme,
    sabitKesinti: data.sabitKesinti,
    extraMesaiYetkisi: data.extraMesaiYetkisi ?? false,
    yillikIzinBaslangicBakiye: data.yillikIzinBaslangicBakiye,
    whatsappBildirimi: data.whatsappBildirimi,
    belgeIdleri: [],
    aktif: true,
    olusturanId,
    olusturmaTarihi: nowServer(),
    guncellenmeTarihi: nowServer(),
  }

  if (data.email?.trim()) docData.email = data.email.trim()
  if (data.adres?.trim()) docData.adres = data.adres.trim()
  if (data.acilIletisim?.isim.trim()) docData.acilIletisim = data.acilIletisim
  if (data.ibanNo?.trim()) docData.ibanNo = data.ibanNo.trim()
  if (data.telegramChatId?.trim()) docData.telegramChatId = data.telegramChatId.trim()

  await setDoc(doc(db, KULLANICILAR, id), docData)

  // Sistem log
  await logYaz({
    tip: 'personel.olustur',
    baslik: `Yeni personel oluşturuldu: ${data.isim.trim()}`,
    hedefId: id,
    hedefIsim: data.isim.trim(),
    yapanId: olusturanId,
    yapanIsim: olusturanIsim,
    yapanRol: olusturanRol,
    ekBilgi: {
      maasTipi: data.maasTipi,
      ucret: data.ucret,
      iseBaslamaTarihi: data.iseBaslamaTarihi,
    },
  })

  return id
}

export async function personelGuncelle(
  id: string,
  data: Partial<PersonelOlusturmaData>,
  yapanId?: string,
  yapanRol: LogRol = 'boss',
  yapanIsim?: string,
): Promise<void> {
  const db = getFirestoreDb()

  // Eski veriyi diff için al
  const eskiSnap = await getDoc(doc(db, KULLANICILAR, id))
  const eski = eskiSnap.exists() ? (eskiSnap.data() as Record<string, unknown>) : {}

  const update: Record<string, unknown> = {
    guncellenmeTarihi: nowServer(),
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return
    if (typeof value === 'string') {
      update[key] = value.trim()
    } else {
      update[key] = value
    }
  })

  await updateDoc(doc(db, KULLANICILAR, id), update)

  // Sistem log — diff hesabı
  if (yapanId) {
    const degisiklikler = degisiklikleriHesapla(eski, update, IZLENEN_ALANLAR)
    if (degisiklikler.length > 0) {
      await logYaz({
        tip: 'personel.guncelle',
        baslik: `Personel güncellendi: ${eski.isim ?? id}`,
        hedefId: id,
        hedefIsim: (eski.isim as string) ?? undefined,
        yapanId,
        yapanIsim,
        yapanRol,
        degisiklikler,
      })
    }
  }
}

export async function personelSil(id: string, yapanId?: string, yapanRol: LogRol = 'boss', yapanIsim?: string): Promise<void> {
  const db = getFirestoreDb()
  const eskiSnap = await getDoc(doc(db, KULLANICILAR, id))
  const eskiIsim = eskiSnap.exists() ? (eskiSnap.data().isim as string) : undefined

  await updateDoc(doc(db, KULLANICILAR, id), {
    aktif: false,
    guncellenmeTarihi: nowServer(),
  })

  if (yapanId) {
    await logYaz({
      tip: 'personel.sil',
      baslik: `Personel silindi (pasif): ${eskiIsim ?? id}`,
      hedefId: id,
      hedefIsim: eskiIsim,
      yapanId,
      yapanIsim,
      yapanRol,
    })
  }
}

export async function personelGeriYukle(id: string, yapanId?: string, yapanRol: LogRol = 'boss', yapanIsim?: string): Promise<void> {
  const db = getFirestoreDb()
  const eskiSnap = await getDoc(doc(db, KULLANICILAR, id))
  const eskiIsim = eskiSnap.exists() ? (eskiSnap.data().isim as string) : undefined

  await updateDoc(doc(db, KULLANICILAR, id), {
    aktif: true,
    guncellenmeTarihi: nowServer(),
  })

  if (yapanId) {
    await logYaz({
      tip: 'personel.geriYukle',
      baslik: `Personel geri yüklendi: ${eskiIsim ?? id}`,
      hedefId: id,
      hedefIsim: eskiIsim,
      yapanId,
      yapanIsim,
      yapanRol,
    })
  }
}
